import { Component } from '@theme/component';

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL'];

/**
 * Size quiz shown on period panty, period swimwear and pregnancy belt product pages.
 * Walks through a few playful questions in a native dialog, recommends a size
 * and can select it in the product's variant picker.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLScriptElement} data
 * @property {HTMLElement[]} steps
 * @property {HTMLElement} progress
 * @property {HTMLElement} count
 * @property {HTMLElement} [chart]
 * @property {HTMLElement} resultTitle
 * @property {HTMLElement} resultWhy
 * @property {HTMLElement} resultNote
 * @property {HTMLElement} resultStock
 * @property {HTMLElement} resultTip
 * @property {HTMLButtonElement} selectButton
 * @property {HTMLButtonElement} backButton
 *
 * @extends {Component<Refs>}
 */
class ClairSizeQuiz extends Component {
  requiredRefs = ['dialog', 'data', 'steps', 'resultTitle', 'selectButton', 'backButton'];

  /** @type {any} */
  #config = null;
  /** @type {Record<string, string>} */
  #answers = {};
  /** @type {string[]} */
  #history = [];
  /** @type {string | null} */
  #size = null;

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#config = JSON.parse(this.refs.data.textContent || '{}');
      // Liquid's t filter HTML-escapes strings; decode them for textContent.
      const decoder = document.createElement('textarea');
      for (const [key, value] of Object.entries(this.#config.t)) {
        decoder.innerHTML = String(value);
        this.#config.t[key] = decoder.value;
      }
    } catch {
      this.#config = null;
    }
  }

  get #first() {
    return this.#config?.kind === 'belt' ? 'stage' : 'usual';
  }

  get #expectedSteps() {
    return 2;
  }

  open() {
    if (!this.#config) return;
    this.restart();
    this.refs.dialog.showModal();
    this.#focusStep();
  }

  close() {
    this.refs.dialog.close();
  }

  restart() {
    this.#answers = {};
    this.#history = [];
    this.#size = null;
    this.#show(this.#first);
  }

  back() {
    this.#history.pop();
    const previous = this.#history.pop();
    if (previous) this.#show(previous);
  }

  /** @param {Event} event */
  answer(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const value = target.dataset.answer;
    const step = target.closest('[data-step]');
    if (!value || !(step instanceof HTMLElement) || !step.dataset.step) return;

    this.#answers[step.dataset.step] = value;
    // A size picked earlier in the flow replaces any fallback answers from a previous attempt.
    if (step.dataset.step === 'usual') {
      delete this.#answers.leggings;
      delete this.#answers.chart;
    } else if (step.dataset.step === 'leggings') {
      delete this.#answers.chart;
    }
    this.#show(this.#next(step.dataset.step, value));
  }

  applySize() {
    const size = this.#size;
    if (!size) return;
    const section = this.closest('.shopify-section') || document;
    const picker = section.querySelector('variant-picker');

    if (picker) {
      for (const select of picker.querySelectorAll('select')) {
        if ([...select.options].some((option) => option.value === size)) {
          select.value = size;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
      for (const input of picker.querySelectorAll('input[type="radio"]')) {
        if (input instanceof HTMLInputElement && input.value === size && !input.checked) {
          input.click();
          break;
        }
      }
    }
    this.close();
  }

  /**
   * @param {string} step
   * @param {string} value
   * @returns {string}
   */
  #next(step, value) {
    switch (step) {
      case 'usual':
        return value === 'unsure' ? 'leggings' : 'between';
      case 'leggings':
        return value === 'unsure' ? 'chart' : 'between';
      case 'chart':
        return 'between';
      case 'stage':
        return 'before';
      default:
        return 'result';
    }
  }

  /** @param {string} name */
  #show(name) {
    if (name === 'chart') this.#renderChart();
    if (name === 'result') this.#renderResult();

    for (const step of this.refs.steps) {
      step.hidden = step.dataset.step !== name;
    }
    this.#history.push(name);

    const isResult = name === 'result';
    const asked = this.#history.filter((step) => step !== 'result' && step !== 'leggings' && step !== 'chart').length;
    const progress = isResult ? 1 : Math.min((asked - 1) / this.#expectedSteps, 0.9);
    if (this.refs.progress) this.refs.progress.style.inlineSize = `${Math.max(progress, 0.06) * 100}%`;
    if (this.refs.count) {
      this.refs.count.hidden = isResult;
      this.refs.count.textContent = this.#t('question', { n: String(Math.min(asked, this.#expectedSteps)) });
    }
    this.refs.backButton.hidden = this.#history.length < 2;

    if (this.refs.dialog.open) this.#focusStep();
  }

  #focusStep() {
    const step = this.refs.steps.find((item) => !item.hidden);
    const heading = step?.querySelector('[tabindex="-1"]');
    if (heading instanceof HTMLElement) heading.focus();
  }

  #renderChart() {
    const chart = this.refs.chart;
    if (!chart || chart.childElementCount) return;
    for (const size of SIZE_ORDER) {
      const cm = this.#config.chart[size];
      if (!cm) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'clair-quiz__option clair-quiz__chart-row';
      button.dataset.answer = size;
      button.setAttribute('on:click', '/answer');
      const label = document.createElement('span');
      label.className = 'clair-quiz__chart-size';
      label.textContent = size;
      const values = document.createElement('span');
      values.className = 'clair-quiz__chart-cm';
      values.textContent = `${this.#t('waist')} ${cm[0]} ${this.#t('cm')} · ${this.#t('hips')} ${cm[1]} ${this.#t('cm')}`;
      button.append(label, values);
      chart.append(button);
    }
  }

  #renderResult() {
    const result = this.#config.kind === 'belt' ? this.#beltResult() : this.#sizeResult();
    this.#size = result.size;

    this.refs.resultTitle.textContent = this.#t('result', { size: result.size });
    this.refs.resultWhy.textContent = result.why;
    this.#setText(this.refs.resultNote, result.note);

    const offered = this.#config.sizes.includes(result.size);
    this.#setText(this.refs.resultStock, offered ? this.#stockText(result.size) : result.notHere);

    this.refs.resultTip.replaceChildren();
    if (result.tip) {
      this.refs.resultTip.append(result.tip.text);
      if (result.tip.url) {
        const link = document.createElement('a');
        link.href = result.tip.url;
        link.textContent = `${this.#t('seeIt')} →`;
        this.refs.resultTip.append(link);
      }
    }
    this.refs.resultTip.hidden = !result.tip;

    this.refs.selectButton.hidden = !offered;
    this.refs.selectButton.textContent = this.#t('select', { size: result.size });
  }

  #sizeResult() {
    const picked = this.#answers.chart || this.#answers.leggings || this.#answers.usual;
    const sizeUp = this.#answers.between === 'between' || this.#answers.between === 'snug';
    let index = SIZE_ORDER.indexOf(picked);
    if (index < 0) index = SIZE_ORDER.indexOf('M');
    if (sizeUp) index = Math.min(index + 1, SIZE_ORDER.length - 1);
    const size = SIZE_ORDER[index];
    const swim = this.#config.kind === 'swim';
    const cm = swim ? null : this.#config.chart[size];

    /** @type {{ text: string, url?: string } | null} */
    let tip = null;
    const alternative = this.#config.alternative;
    if (
      !this.#config.sizes.includes(size) &&
      alternative?.url &&
      alternative.handle !== this.#config.current &&
      Array.isArray(alternative.sizes) &&
      alternative.sizes.includes(size)
    ) {
      tip = { text: this.#t('tryOther', { product: alternative.title, size }), url: alternative.url };
    }

    let why = this.#t(swim ? 'whySwim' : 'whySame');
    if (sizeUp) why = this.#t('whyUp');

    return {
      size,
      why,
      note: cm ? this.#t('measure', { size, waist: String(cm[0]), hips: String(cm[1]) }) : '',
      notHere: this.#t('notHere', { sizes: this.#config.sizes.join(', '), size }),
      tip,
    };
  }

  #beltResult() {
    const stage = this.#answers.stage;
    const before = this.#answers.before;
    const large = stage === 't3' || before === 'xl';
    const size = large ? 'L/XL' : 'S/M';
    const small = !large;
    let note = '';
    if (stage === 'after') note = this.#t('tipAfter');
    else if (small) note = this.#t('tipGrow');

    return {
      size,
      why: this.#t(small ? 'whyBeltSmall' : 'whyBeltLarge'),
      note,
      notHere: this.#t('notHere', { sizes: this.#config.sizes.join(', '), size }),
      tip: null,
    };
  }

  /** @param {string} size */
  #stockText(size) {
    const variants = this.#config.variants.filter((/** @type {any} */ v) => v.size === size);
    const available = variants.filter((/** @type {any} */ v) => v.available);
    if (!available.length) return this.#t('soldOut', { size });
    const colors = [...new Set(available.map((/** @type {any} */ v) => v.other).filter(Boolean))];
    return colors.length ? this.#t('inStock', { size, colors: colors.join(', ') }) : '';
  }

  /**
   * @param {HTMLElement} element
   * @param {string} text
   */
  #setText(element, text) {
    element.textContent = text || '';
    element.hidden = !text;
  }

  /**
   * @param {string} key
   * @param {Record<string, string>} [values]
   */
  #t(key, values = {}) {
    let text = this.#config.t[key] || '';
    for (const [name, value] of Object.entries(values)) {
      text = text.replaceAll(`{${name}}`, value);
    }
    return text;
  }
}

if (!customElements.get('clair-size-quiz')) {
  customElements.define('clair-size-quiz', ClairSizeQuiz);
}
